import React from "react";

import { css } from "@emotion/css";
import { CellContext, type SortingFn } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { UnreadMessageCount } from "@/models/MessagingModel.ts";
import { PatientListItem } from "@/models/PatientSummaryModel.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Tag } from "@/uiKit/atoms/Tag.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";

export type MessagesColumnProps = CellContext<PatientListItem, UnreadMessageCount>;

export const DoctorMessagesColumn: React.FC<MessagesColumnProps> = ({ getValue }) => {
  const styles = useStyles(makeStyles);
  const unreadMessages = getValue();
  const count = unreadMessages.fromPatient + unreadMessages.fromPractitioner;

  if (count <= 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      <Tag
        color="blue"
        label={
          <div className={styles.messageTagIcon}>
            <Icons.letter className={styles.icon} />
            <Typography variant="body">{count}</Typography>
          </div>
        }
      />
    </div>
  );
};

export const DoctorMessagesHeader: React.FC = () => {
  const { t } = useTranslation();
  const styles = useStyles(makeHeaderStyles);

  return (
    <div className={styles.container}>
      <Typography>{t("pages.patients.messages")}</Typography>
    </div>
  );
};

const unreadTotal = (count: UnreadMessageCount) => count.fromPatient + count.fromPractitioner;

export const sortByDoctorUnreadMessages: SortingFn<PatientListItem> = (rowA, rowB) =>
  unreadTotal(rowB.original.unreadMessages) - unreadTotal(rowA.original.unreadMessages);

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
  `,
  messageTagIcon: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  icon: css`
    width: ${theme.typography.body.fontSize};
    height: ${theme.typography.body.fontSize};
  `,
});

const makeHeaderStyles = () => ({
  container: css`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
  `,
});
